import {
  actionColumn,
  BoundNumberField,
  ButtonMenu,
  Chip,
  collapseColumn,
  column,
  Css,
  DocumentScrollOverlayRightPaneLayout,
  emptyCell,
  GridCellContent,
  GridColumn,
  GridTableApi,
  GridTableLayout,
  HeaderAction,
  IconButton,
  numericColumn,
  PinToggle,
  RowStyles,
  selectColumn,
  Tag,
  Tooltip,
  useComputed,
  useModal,
  useToast,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormStates } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { emptyCellDash } from "src/components";
import { Icon } from "src/components/Icon";
import {
  ItivOrderField,
  PotentialOperation2,
  SaveTakeoffLineItemInput,
  TakeoffLineItemAction,
  TakeoffLineItemFilter,
} from "src/generated/graphqlTypes";
import { useCsvExportPost } from "src/hooks/useCsvExportPost";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import {
  addLineMenuItems,
  lineTypeDetails,
  lineTypeOf,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/lineTypes";
import { useTakeoffsSidePanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/useTakeoffsSidePanel";
import { useImportItems } from "src/routes/libraries/plan-package-v2/components/takeoffs/useImportItems";
import { useTakeoffsLayoutState } from "src/routes/libraries/plan-package-v2/components/takeoffs/useTakeoffsLayoutState";
import {
  LineType,
  useTakeoffRemovalsEnabled,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { BulkTaskAllocationModal } from "src/routes/libraries/plan-package/takeoffs/components/BulkTaskAllocationModal/BulkTaskAllocationModal";
import { DeleteItemsConfirmationModal } from "src/routes/libraries/plan-package/takeoffs/components/DeleteItemsConfirmationModal";
import { DuplicateItemsConfirmationModal } from "src/routes/libraries/plan-package/takeoffs/components/DuplicateItemsConfirmationModal";
import {
  applyStyling,
  changedCellCss,
  createRows,
  formatQuantityDelta,
  TakeoffLineItemRows,
} from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/PlanPackageTakeoffTable";
import {
  PlanPackageTakeoffTable_GroupTotalsFragment,
  PlanPackageTakeoffTable_ItemsFragment,
  PlanPackageTakeoffTableQuery,
  usePlanPackageTakeoffTableQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/PlanPackageTakeoffTable.generated";
import { PlanPackageTakeoffTableEmpty } from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/PlanPackageTakeoffTableEmpty";
import {
  useSelectedTliIds,
  useTakeoffsManagerContext,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { maybeAddIdColumn } from "src/utils/idColumn";
import { subFilterByOrder } from "src/utils/itemTemplateItem";

export function TakeoffsTable() {
  const takeoffRemovalsEnabled = useTakeoffRemovalsEnabled();
  const canEditLineItems = useTakeoffsStore((state) => state.planPackage.canEditLineItems);
  const isEditable = canEditLineItems.allowed;
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const versionId = useTakeoffsStore((state) => state.planPackage.version.id);
  const includeRemoved = useTakeoffsStore((state) => state.includeRemoved);
  // Not using `useTakeoffsStore` to avoid unnecessary reactivity
  const takeoffsStore = useTakeoffsManagerContext();
  const { layoutState, sorting, filter, order, groupBy } = useTakeoffsLayoutState();
  const { openModal } = useModal();
  const { activeTliId, openEdit, openAdd, openDuplicate, openBulkEdit, lineDeleted } = useTakeoffsSidePanel();

  const query = usePlanPackageTakeoffTableQuery({
    variables: { versionId, filter: { readyPlan: [planPackageId], includeRemoved, ...filter }, first: 25, order },
    fetchPolicy: "cache-and-network",
  });
  const { data, fetchMore } = query;

  const { showToast } = useToast();
  const failedGroupIds = useRef(new Set<string>());
  // Skip groups whose fetch hasn't come back yet, i.e. a loading row remounting while it scrolls
  const loadingGroupIds = useRef(new Set<string>());
  const fetchMoreGroupItems = useCallback(
    async (groupTotals: PlanPackageTakeoffTable_GroupTotalsFragment) => {
      const { groupId } = groupTotals;
      if (failedGroupIds.current.has(groupId) || loadingGroupIds.current.has(groupId)) return;
      loadingGroupIds.current.add(groupId);
      try {
        await fetchMore({ variables: { first: null, subFilter: subFilterByOrder(groupBy, groupId) } });
      } catch (e) {
        console.error(e);
        failedGroupIds.current.add(groupId);
        showToast({ type: "error", message: `Failed to fetch items for group ${groupTotals.displayName}` });
      } finally {
        loadingGroupIds.current.delete(groupId);
      }
    },
    [fetchMore, showToast, groupBy],
  );

  const deleteLine = useCallback(
    (tliId: string) =>
      openModal({
        content: (
          <DeleteItemsConfirmationModal
            store={takeoffsStore}
            itemIdsToDelete={[tliId]}
            onDeleted={() => lineDeleted(tliId)}
          />
        ),
      }),
    [openModal, takeoffsStore, lineDeleted],
  );
  const importItems = useImportItems();

  // Our only inline edit is quantiy. All other edits happen in side panel
  const { getFormState } = useFormStates<QuantityFormValue, PlanPackageTakeoffTable_ItemsFragment>({
    config: quantityFormConfig,
    getId: (tli) => tli.id,
    map: (tli) => ({ id: tli.id, quantity: tli.quantity }),
    autoSave: async (os) => {
      await takeoffsStore.getState().saveTli(os.changedValue);
    },
  });

  const columns = useMemo(
    () =>
      createColumns({
        fetchMoreGroupItems,
        deleteLine,
        importItems,
        openAdd,
        openDuplicate,
        getFormState,
        isEditable,
        // Highlight changes only when viewing a draft or previous version
        highlightChanges: includeRemoved,
        takeoffRemovalsEnabled,
      }),
    [
      fetchMoreGroupItems,
      deleteLine,
      importItems,
      openAdd,
      openDuplicate,
      getFormState,
      isEditable,
      includeRemoved,
      takeoffRemovalsEnabled,
    ],
  );
  const { itemTableApi } = takeoffsStore.getState();
  const selectedTliIds = useSelectedTliIds(itemTableApi);
  const createTableRows = useCallback(
    (data: PlanPackageTakeoffTableQuery | undefined) => {
      const { items = [], groupTotals = [] } = data?.takeoffLineItemsPage ?? {};
      return createRows(items, groupTotals, takeoffsStore, selectedTliIds);
    },
    [takeoffsStore, selectedTliIds],
  );
  const rowStyles: RowStyles<TakeoffLineItemRows> = useMemo(
    () => ({
      item: {
        // Added and removed lines style every cell, including Beam's select column; changed cells are styled per column
        cellCss: (row) => (includeRemoved ? applyStyling(row.data) : undefined) ?? {},
        onClick: (row, api) => {
          // Removed lines have no actions, same as v1
          if (row.data.changeType === AggregateMemberVersionChangeType.REMOVED) return;
          // While anything is selected, a row click toggles the row rather than opening the pane
          const selectedIds = api.getSelectedRowIds();
          if (selectedIds.nonEmpty) {
            api.selectRow(row.id, !selectedIds.includes(row.id));
          } else {
            openEdit(row.id);
          }
        },
      },
    }),
    [openEdit, includeRemoved],
  );
  // Exports the selection, or the current filter when nothing is selected
  const csvParameters = useMemo(
    () => ({
      filter: { ...filter, ...(selectedTliIds.nonEmpty && { id: selectedTliIds }) } satisfies TakeoffLineItemFilter,
      versionId,
    }),
    [filter, selectedTliIds, versionId],
  );
  const { handleDownload } = useCsvExportPost("takeoff", csvParameters);
  const actions = useMemo(
    () =>
      createToolbarActions(selectedTliIds, canEditLineItems, {
        onBulkEdit: openBulkEdit,
        onDuplicate: () => openModal({ content: <DuplicateItemsConfirmationModal store={takeoffsStore} /> }),
        onAllocateTask: () =>
          openModal({ content: <BulkTaskAllocationModal store={takeoffsStore} tliIdsToEdit={selectedTliIds} /> }),
        onDownload: handleDownload,
      }),
    [selectedTliIds, canEditLineItems, openBulkEdit, openModal, takeoffsStore, handleDownload],
  );

  // An unfiltered plan with no lines gets the CSV drop zone; a filter that matches nothing keeps the table
  const hasGroups = data?.takeoffLineItemsPage.groupTotals.some((gt) => gt.groupId !== queryTotalGroupId);
  if (data && !hasGroups && Object.keys(filter).isEmpty) {
    // The header's Add menu still opens the side panel here
    return (
      <DocumentScrollOverlayRightPaneLayout paneWidth={paneWidth}>
        <PlanPackageTakeoffTableEmpty />
      </DocumentScrollOverlayRightPaneLayout>
    );
  }

  return (
    <GridTableLayout
      layoutState={layoutState}
      actions={actions}
      selectionSummary
      emptyFallback="No items match the applied filters. Please adjust and try again"
      tableProps={{
        query,
        createRows: createTableRows,
        columns,
        rowStyles,
        sorting,
        api: itemTableApi,
        activeRowId: activeTliId ? `item_${activeTliId}` : undefined,
        as: "virtual",
        style: { grouped: true, bordered: true, rowHeight: "fixed" },
      }}
      withRightPane={paneWidth}
    />
  );
}

enum ColumnId {
  Pin = "pin",
  Id = "id",
  CostCode = "costCode",
  ItemCode = "itemCode",
  Type = "type",
  Name = "name",
  MaterialCode = "materialCode",
  Quantity = "quantity",
  UnitOfMeasure = "unitOfMeasure",
  Location = "location",
  Option = "option",
  TaskAllocation = "taskAllocation",
  Actions = "actions",
}

type CreateColumnsProps = {
  fetchMoreGroupItems: (groupTotals: PlanPackageTakeoffTable_GroupTotalsFragment) => Promise<void>;
  deleteLine: (tliId: string) => void;
  importItems: VoidFunction;
  openAdd: (type: LineType) => void;
  openDuplicate: (tliId: string) => void;
  getFormState: (tli: PlanPackageTakeoffTable_ItemsFragment) => ObjectState<QuantityFormValue>;
  isEditable: boolean;
  highlightChanges: boolean;
  takeoffRemovalsEnabled: boolean;
};

function createColumns(props: CreateColumnsProps): GridColumn<TakeoffLineItemRows>[] {
  const {
    fetchMoreGroupItems,
    deleteLine,
    importItems,
    openAdd,
    openDuplicate,
    getFormState,
    isEditable,
    highlightChanges,
    takeoffRemovalsEnabled,
  } = props;
  return [
    collapseColumn<TakeoffLineItemRows>({ name: "Collapse", sticky: "left" }),
    // The pin used to live in the collapse column. Beam draws that column inside the first content
    // cell and replaces it with a spacer on rows that have no chevron, which hid the pin on item rows.
    ...maybeAddIdColumn(
      column<TakeoffLineItemRows>({
        id: ColumnId.Id,
        name: "Id",
        canHide: false,
        header: "Id",
        groupBy: emptyCell,
        item: (tli) => tli.id,
        loading: emptyCell,
        w: "100px",
      }),
    ),
    selectColumn<TakeoffLineItemRows>({
      name: "Select",
      sticky: "left",
      // Loading rows aren't selectable, so skip the disabled checkbox Beam would show
      loading: emptyCell,
    }),
    // Data columns keep the design's proportions (`w` in fr), shrinking no further than `mw`.
    // A `serverSideSortKey` makes the header sortable; it's the `ItivOrderField` that goes into the query and
    // `?sortBy=`. Sorting is server-side because groups load lazily, so the table never holds every line.
    column<TakeoffLineItemRows>({
      id: ColumnId.CostCode,
      name: "Cost Code",
      canHide: false,
      sticky: "left",
      header: "Cost Code",
      groupBy: (gt, { api }) => ({
        content: () => <GroupLabel displayName={gt.displayName} />,
        // Span through Name so long group names have room
        colspan: api.getVisibleColumnIds().includes(ColumnId.Type) ? 4 : 3,
      }),
      item: (tli, { api }) => ({
        content: () => (
          <span css={Css.df.aic.gap2.relative.$}>
            {/* TODO: Remove this once we have direct support for table row pinning: https://linear.app/homebound-team/issue/DS-441/table-row-pinning-direct-support */}
            <div css={Css.absolute.left(-9).$}>
              <RowPinToggle api={api} rowId={tli.id} />
            </div>
            {tli.item.costCode.number}
            {takeoffRemovalsEnabled && tli.action.code === TakeoffLineItemAction.Remove && <Chip text="Removal" />}
          </span>
        ),
      }),
      // Lives in a column that can't be hidden, so a group's lines always get fetched once it's expanded
      loading: (gt) => ({
        content: () => <LoadingGroupCell groupTotals={gt} fetchMoreGroupItems={fetchMoreGroupItems} />,
      }),
      serverSideSortKey: ItivOrderField.CostCode,
      w: 161,
      mw: "160px",
    }),
    column<TakeoffLineItemRows>({
      id: ColumnId.ItemCode,
      name: "Item Code",
      canHide: false,
      sticky: "left",
      header: "Item Code",
      groupBy: emptyCell,
      item: (tli) => ({
        content: () => (
          <Tooltip placement="top" title={tli.item.name}>
            <span css={Css.truncate.$}>{tli.item.fullCode}</span>
          </Tooltip>
        ),
      }),
      loading: emptyCell,
      serverSideSortKey: ItivOrderField.Item,
      w: 106,
      mw: "100px",
    }),
    // Type and Name don't sort: the server has no order for either (Item sorts by code, then name)
    column<TakeoffLineItemRows>({
      id: ColumnId.Type,
      name: "Type",
      header: "Type",
      groupBy: emptyCell,
      item: (tli) => lineTypeDetails[lineTypeOf(tli)].label,
      loading: emptyCell,
      w: 106,
      mw: "100px",
    }),
    column<TakeoffLineItemRows>({
      id: ColumnId.Name,
      name: "Name",
      canHide: false,
      header: "Name",
      groupBy: emptyCell,
      item: (tli) => tli.name,
      loading: emptyCell,
      w: 198,
      mw: "180px",
    }),
    column<TakeoffLineItemRows>({
      id: ColumnId.MaterialCode,
      name: "Material Code",
      header: "Material Code",
      groupBy: emptyCell,
      item: (tli) => ({
        ...materialCodeCell(tli),
        css: highlightChanges ? changedCellCss(tli, "materialVariant") : undefined,
      }),
      loading: emptyCell,
      serverSideSortKey: ItivOrderField.MaterialCode,
      w: 149,
      mw: "130px",
    }),
    numericColumn<TakeoffLineItemRows>({
      id: ColumnId.Quantity,
      name: "Qty",
      header: "Qty",
      groupBy: emptyCell,
      item: (tli) => {
        const canEditInline =
          isEditable &&
          tli.unitOfMeasure?.useQuantity &&
          tli.changeType !== AggregateMemberVersionChangeType.REMOVED &&
          // The backend clears a Remove line's Qty
          !(takeoffRemovalsEnabled && tli.action.code === TakeoffLineItemAction.Remove);
        return {
          content: () =>
            canEditInline ? (
              <BoundNumberField
                field={getFormState(tli).quantity}
                displayDirection
                readOnly={disableBasedOnPotentialOperation(tli.canEditQuantity)}
              />
            ) : tli.unitOfMeasure?.useQuantity ? (
              formatQuantityDelta(tli.quantity)
            ) : (
              "N/A"
            ),
          css: highlightChanges ? changedCellCss(tli, "quantity") : undefined,
        };
      },
      loading: emptyCell,
      serverSideSortKey: ItivOrderField.Quantity,
      w: 81,
      mw: "80px",
      // Clicking into the field edits it rather than opening the line
      wrapAction: false,
    }),
    column<TakeoffLineItemRows>({
      id: ColumnId.UnitOfMeasure,
      name: "UoM",
      header: "UoM",
      groupBy: emptyCell,
      item: (tli) => ({
        content: tli.unitOfMeasure?.abbreviation,
        css: highlightChanges ? changedCellCss(tli, "unitOfMeasure") : undefined,
      }),
      loading: emptyCell,
      serverSideSortKey: ItivOrderField.UnitOfMeasure,
      w: 81,
      mw: "80px",
    }),
    column<TakeoffLineItemRows>({
      id: ColumnId.Location,
      name: "Location",
      header: "Location",
      groupBy: emptyCell,
      item: (tli) => ({
        content: tli.location?.displayLocationPath,
        css: highlightChanges ? changedCellCss(tli, "location") : undefined,
      }),
      loading: emptyCell,
      serverSideSortKey: ItivOrderField.Location,
      w: 122,
      mw: "120px",
    }),
    column<TakeoffLineItemRows>({
      id: ColumnId.Option,
      name: "Option",
      header: "Option",
      groupBy: emptyCell,
      item: (tli) => ({ ...optionsCell(tli), css: highlightChanges ? changedCellCss(tli, "options") : undefined }),
      loading: emptyCell,
      serverSideSortKey: ItivOrderField.Options,
      w: 106,
      mw: "120px",
    }),
    column<TakeoffLineItemRows>({
      id: ColumnId.TaskAllocation,
      name: "Task Allocation",
      header: "Task Allocation",
      groupBy: emptyCell,
      item: (tli) => ({ content: tli.task?.name, css: highlightChanges ? changedCellCss(tli, "task") : undefined }),
      loading: emptyCell,
      serverSideSortKey: ItivOrderField.GlobalPlanTask,
      w: 248,
      mw: "180px",
    }),
    ...(isEditable
      ? [
          actionColumn<TakeoffLineItemRows>({
            id: ColumnId.Actions,
            name: "Actions",
            header: emptyCell,
            groupBy: {
              content: () => (
                <div css={Css.df.gap1.$}>
                  <IconButton icon="upload" label="Import items" onClick={importItems} />
                  <ButtonMenu
                    trigger={{ icon: "plus" }}
                    tooltip="Add line"
                    items={addLineMenuItems(openAdd)}
                    placement="right"
                  />
                </div>
              ),
              sticky: "right",
            },
            item: (tli) =>
              tli.changeType === AggregateMemberVersionChangeType.REMOVED
                ? emptyCell
                : {
                    content: () => (
                      <div css={Css.df.gap1.$}>
                        <IconButton
                          inc={2}
                          icon="duplicate"
                          label="Duplicate"
                          onClick={() => openDuplicate(tli.id)}
                          disabled={disableBasedOnPotentialOperation(tli.canDuplicate)}
                        />
                        <IconButton inc={2} icon="trash" label="Delete" onClick={() => deleteLine(tli.id)} />
                      </div>
                    ),
                  },
            loading: emptyCell,
            w: "80px",
          }),
        ]
      : []),
  ];
}

type ToolbarHandlers = {
  onBulkEdit: VoidFunction;
  onDuplicate: VoidFunction;
  onAllocateTask: VoidFunction;
  onDownload: VoidFunction;
};

/** The table toolbar's bulk ⋮, after the columns selector. */
function createToolbarActions(
  selectedTliIds: string[],
  canEditLineItems: PotentialOperation2,
  handlers: ToolbarHandlers,
): HeaderAction[] {
  const { onBulkEdit, onDuplicate, onAllocateTask, onDownload } = handlers;
  const readOnlyReason = disableBasedOnPotentialOperation(canEditLineItems);
  // A read-only version is the reason to show over an empty selection, since selecting rows won't help
  const needsSelection = (verb: string) => readOnlyReason || (selectedTliIds.isEmpty && `Select rows to ${verb}`);

  return [
    {
      kind: "menu",
      trigger: { icon: "verticalDots", variant: "outline" },
      items: [
        { label: "Bulk Edit", icon: "pencil", onClick: onBulkEdit, disabled: needsSelection("edit") },
        { label: "Duplicate", icon: "duplicate", onClick: onDuplicate, disabled: needsSelection("duplicate") },
        { label: "Allocate Task", icon: "task", onClick: onAllocateTask, disabled: needsSelection("allocate a task") },
        // Never disabled: v1 exports the current filter when nothing is selected, and exporting doesn't edit
        { label: "Download to .csv", icon: "download", onClick: onDownload },
      ],
    },
  ];
}

type GroupLabelProps = { displayName: string };

function GroupLabel(props: GroupLabelProps) {
  const { displayName } = props;
  const label = <span css={Css.smSb.truncate.$}>{displayName}</span>;
  // Option groups join every option's name, so list them out when the label is likely truncated
  if (displayName.length <= 55) return label;
  return (
    <Tooltip
      title={
        <ul css={Css.pl2.$}>
          {displayName.split(",").map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      }
    >
      {label}
    </Tooltip>
  );
}

type LoadingGroupCellProps = Pick<CreateColumnsProps, "fetchMoreGroupItems"> & {
  groupTotals: PlanPackageTakeoffTable_GroupTotalsFragment;
};

/** Fetches the rest of a group's lines once its loading row is on screen. */
function LoadingGroupCell(props: LoadingGroupCellProps) {
  const { groupTotals, fetchMoreGroupItems } = props;
  useEffect(() => {
    fetchMoreGroupItems(groupTotals).catch(console.error);
  }, [groupTotals, fetchMoreGroupItems]);
  return (
    <span css={Css.df.aic.gap1.$}>
      Loading...
      <Icon icon="refresh" />
    </span>
  );
}

type RowPinToggleProps = { api: GridTableApi<TakeoffLineItemRows>; rowId: string };

/** Beam's pin toggle, revealed on hover on desktop; smaller screens and pinned rows always show it. */
function RowPinToggle(props: RowPinToggleProps) {
  const { api, rowId } = props;
  // Each row gets a fresh `api` object, but its methods are bound once, so depend on the method itself
  const { isPinnedRow } = api;
  const isPinned = useComputed(() => isPinnedRow(rowId), [isPinnedRow, rowId]);
  return (
    <div css={isPinned ? pinCellCss : revealPinOnHoverCss}>
      <PinToggle rowId={rowId} />
    </div>
  );
}

function materialCodeCell(tli: PlanPackageTakeoffTable_ItemsFragment): GridCellContent {
  const { materialVariant } = tli;
  if (!materialVariant) return { content: emptyCellDash };
  const attrs = materialVariant.materialAttributeValues;
  const title =
    !attrs || attrs.isEmpty
      ? materialVariant.code
      : attrs.map(({ textValue, dimension }) => (
          <div key={textValue}>
            {dimension.name}: {textValue} {dimension.unitOfMeasure?.abbreviation}
          </div>
        ));
  return {
    content: () => (
      <Tooltip placement="top" title={title}>
        <span css={Css.truncate.$}>{materialVariant.code}</span>
      </Tooltip>
    ),
  };
}

function optionsCell(tli: PlanPackageTakeoffTable_ItemsFragment): GridCellContent {
  const [first, ...others] = tli.options;
  if (!first) return { content: "--" };
  const tags = (
    <div css={Css.df.aic.gap1.$}>
      <Tag variant="secondary" text={optionLabel(first)} />
      {others.nonEmpty && <span>+{others.length}</span>}
    </div>
  );
  return {
    content: () =>
      others.isEmpty ? (
        tags
      ) : (
        <Tooltip
          title={
            <div css={Css.df.fdc.gapPx(2).$}>
              {tli.options.map((rpo) => (
                <div key={rpo.id}>{optionLabel(rpo)}</div>
              ))}
            </div>
          }
        >
          {tags}
        </Tooltip>
      ),
  };
}

function optionLabel(rpo: PlanPackageTakeoffTable_ItemsFragment["options"][number]): string {
  return rpo.active ? rpo.displayCode : `${rpo.displayCode} (Archived)`;
}

/** The page-wide total the server returns alongside the real groups. */
const queryTotalGroupId = "queryTotal";

const paneWidth = 380;

// Bleeds over the cell padding so hovering anywhere in the left cell reveals the pin
const pinCellCss = Css.df.aic.jcc.fg1.h100.mxPx(-12).$;
const revealPinOnHoverCss = Css.df.aic.jcc.fg1.h100
  .mxPx(-12)
  .o0.when({ ":hover": Css.o100.$, ":focus-within": Css.o100.$ }).ifMdAndDown.o100.$;

type QuantityFormValue = Pick<SaveTakeoffLineItemInput, "id" | "quantity">;

// Beam's number field rounds a typed fraction, so a set Qty is already the whole number the schema's `Int` wants
const quantityFormConfig: ObjectConfig<QuantityFormValue> = {
  id: { type: "value" },
  quantity: { type: "value", rules: [required] },
};
